/**
 * The two arche governance kill switches (ADR-026), on the Dev Console's
 * Arche Management page:
 *
 *   arche_restriction_enabled       may an arche be NEWLY restricted
 *   company_arche_transfer_enabled  may an arche be offered to, or accepted by,
 *                                   a Company
 *
 * Both are CREATION-ONLY. Turning one off stops new restrictions or new
 * transfers; every existing restriction stays enforced, every company-owned
 * arche stays governed, grants and releases keep working, and an arche can
 * always be opened up again. Turning one on widens what people can do, so it
 * asks first; turning one off is one click, because that is the emergency
 * direction. The server reads OFF when the row is missing or unreadable.
 *
 * Server state lives in React Query (CLAUDE.md "Data Fetching"): the settings
 * are cached under `queryKeys.adminArcheGovernance.settings()` and the
 * mutation writes the server's answer back into that cache.
 */

import { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import api, { type ArcheGovernanceSettingKey, type ArcheGovernanceSettingsDto } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

const SWITCHES: Array<{
  key: ArcheGovernanceSettingKey;
  title: string;
  on: string;
  off: string;
  confirmOn: string;
}> = [
  {
    key: 'arche_restriction_enabled',
    title: 'Restricting arches',
    on: 'Arche owners and Companies can restrict an arche to chosen people, workspaces, Companies and guilds.',
    off: 'Nobody can newly restrict an arche. Arches already restricted stay restricted and enforced; their grants can still be managed, and any of them can be opened up again.',
    confirmOn:
      'Arche owners and Company admins will be able to restrict arches: a restricted arche leaves the Store and search, its site pages come down, and only its team and grantees can open it.',
  },
  {
    key: 'company_arche_transfer_enabled',
    title: 'Moving arches to Companies',
    on: 'A creator can offer an arche to a Company, and a Company owner can accept.',
    off: 'No new offers, and open offers cannot be accepted (they stay open). Arches a Company already owns stay governed by it; releasing one back to a person always works.',
    confirmOn:
      'Creators will be able to offer arches to Companies and Company owners to accept them, moving the arche’s earnings to the Company’s payer.',
  },
];

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}

export function ArcheGovernanceSwitches() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: queryKeys.adminArcheGovernance.settings(),
    queryFn: () => api.adminGetArcheGovernanceSettings(),
    retry: false,
  });
  const setEnabled = useMutation({
    mutationFn: (input: { key: ArcheGovernanceSettingKey; enabled: boolean }) =>
      api.adminSetArcheGovernanceSetting(input.key, input.enabled),
    onSuccess: (result) => {
      // The server's answer is the truth; write it into the cache rather than
      // trusting what was asked for.
      qc.setQueryData<ArcheGovernanceSettingsDto>(
        queryKeys.adminArcheGovernance.settings(),
        (prev) => (prev ? { ...prev, [result.key]: result.enabled } : prev),
      );
      const title = SWITCHES.find((s) => s.key === result.key)?.title ?? result.key;
      toast({ title: `${title}: ${result.enabled ? 'on' : 'off'}` });
    },
    onError: (err, input) => {
      const title = SWITCHES.find((s) => s.key === input.key)?.title ?? input.key;
      toast({
        title: `Could not change “${title}”`,
        description: errorMessage(err),
        variant: 'destructive',
      });
    },
  });
  const [confirmOn, setConfirmOn] = useState<ArcheGovernanceSettingKey | null>(null);

  function handleToggle(key: ArcheGovernanceSettingKey, next: boolean): void {
    // Widening asks first; stopping is one click.
    if (next) setConfirmOn(key);
    else setEnabled.mutate({ key, enabled: false });
  }

  const pendingOn = SWITCHES.find((s) => s.key === confirmOn);
  const data = settings.data;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="h-4 w-4" aria-hidden="true" />
          Governance switches
        </CardTitle>
        <CardDescription>
          Each switch only gates NEW restrictions or NEW ownership moves. Nothing already in place
          is undone by turning one off, and opening an arche up or releasing one to a person is
          never gated. Off is the safe reading: the server treats a missing or unreadable setting as
          off.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {settings.isError ? (
          <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
            <p>Could not load the governance switches: {errorMessage(settings.error)}</p>
            <Button
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={() => void settings.refetch()}
            >
              Retry
            </Button>
          </div>
        ) : !data ? (
          <div className="animate-pulse space-y-3" aria-label="Loading governance switches">
            <div className="h-5 w-48 rounded bg-muted" />
            <div className="h-5 w-56 rounded bg-muted" />
          </div>
        ) : (
          SWITCHES.map((s) => {
            const enabled = data[s.key] === true;
            const id = `governance-${s.key}`;
            return (
              <div
                key={s.key}
                className="flex items-start justify-between gap-4 rounded-lg border border-border p-3"
              >
                <div className="min-w-0">
                  <Label htmlFor={id} className="flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className={`inline-flex h-2.5 w-2.5 rounded-full ${
                        enabled ? 'bg-amber-500' : 'bg-muted-foreground/40'
                      }`}
                    />
                    {s.title} is{' '}
                    <span data-testid={`${s.key}-status`} className="font-semibold">
                      {enabled ? 'On' : 'Off'}
                    </span>
                  </Label>
                  <p className="mt-1 text-xs text-muted-foreground">{enabled ? s.on : s.off}</p>
                </div>
                <Switch
                  id={id}
                  aria-label={s.title}
                  checked={enabled}
                  disabled={setEnabled.isPending}
                  onCheckedChange={(next) => handleToggle(s.key, next)}
                />
              </div>
            );
          })
        )}
        <p className="text-xs text-muted-foreground">
          {setEnabled.isPending
            ? 'Saving…'
            : 'Takes effect on this server immediately; other servers pick it up within about 30 seconds. Every change is audited.'}
        </p>
      </CardContent>

      <AlertDialog open={confirmOn !== null} onOpenChange={(open) => !open && setConfirmOn(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Turn on “{pendingOn?.title}”?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingOn?.confirmOn} You can turn it off again with one click; doing so never undoes
              what was already done while it was on.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const key = confirmOn;
                setConfirmOn(null);
                if (key) setEnabled.mutate({ key, enabled: true });
              }}
            >
              Turn on
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
